// CompetitionContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { FALLBACK_ACTIVE_SEASON } from '../constants/seasons';
import { auth, getIdToken } from './Firebase'; // Zakładam, że getIdToken jest dostępny

export const CompetitionContext = createContext();

export function CompetitionProvider({ children }) {
  const [competitions, setCompetitions] = useState({});
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [settings, setSettings] = useState({ activeSeason: null });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const fetchSettings = async (headers) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/app-settings`, { headers });
      return response.data || { activeSeason: FALLBACK_ACTIVE_SEASON };
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        return { activeSeason: process.env.REACT_APP_ACTIVE_SEASON || FALLBACK_ACTIVE_SEASON };
      }
      throw error;
    }
  };

  useEffect(() => {
    if (authLoading) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = await getIdToken(); // Pobierz token JWT
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const [settingsResponse, response] = await Promise.all([
          fetchSettings(headers),
          axios.get(`${process.env.REACT_APP_API_URL}/api/competitions/nav`, { headers })
        ]);
        setSettings(settingsResponse || { activeSeason: FALLBACK_ACTIVE_SEASON });
        setCompetitions(response.data);
      } catch (error) {
        console.error('Error fetching competitions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading]);

  const updateCompetition = (competitionId, newSimulationId) => {
    setCompetitions(prev => {
      const updatedCompetitions = { ...prev };
      for (const level of Object.keys(updatedCompetitions)) {
        const comps = updatedCompetitions[level] || [];
        const compIndex = comps.findIndex(comp => comp.competition_id === competitionId);
        if (compIndex !== -1) {
          const nextComps = [...comps];
          const comp = { ...nextComps[compIndex] };
          comp.simulation_ids = [...(comp.simulation_ids || []), { simulation_id: newSimulationId, created_at: new Date().toISOString(), conditions: "" }];
          comp.latest_simulation_id = newSimulationId;
          nextComps[compIndex] = comp;
          return { ...updatedCompetitions, [level]: nextComps };
        }
      }
      return prev;
    });
  };

  const refreshCompetitions = async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [settingsResponse, response] = await Promise.all([
        fetchSettings(headers),
        axios.get(`${process.env.REACT_APP_API_URL}/api/competitions/nav`, { headers })
      ]);
      setSettings(settingsResponse || { activeSeason: FALLBACK_ACTIVE_SEASON });
      setCompetitions(response.data);
    } catch (error) {
      console.error('Error refreshing competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompetitionContext.Provider value={{ competitions, loading, settings, activeSeason: settings.activeSeason, updateCompetition, refreshCompetitions }}>
      {children}
    </CompetitionContext.Provider>
  );
}

export const useCompetitions = () => useContext(CompetitionContext);
